import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  ArrowRight, 
  CheckCircle, 
  Star,
  Users,
  BookOpen,
  Award,
  Zap,
  Shield
} from 'lucide-react';

const CtaSection = ({ company }) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const benefits = [
    'Access to 50+ Expert-Led Courses',
    'Industry-Recognized Certifications',
    '24/7 Learning Support',
    'Career Guidance & Job Placement',
    'Lifetime Access to Materials',
    'Mobile Learning App'
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };

  const floatingVariants = {
    animate: {
      y: [-10, 10, -10],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    }
  };

  return (
    <section ref={ref} className="section-padding bg-gradient-to-br from-primary-600 via-primary-700 to-accent-600 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-hero-pattern opacity-10"></div>
      
      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          variants={floatingVariants}
          animate="animate"
          className="absolute top-20 left-20 w-32 h-32 bg-white/10 rounded-full blur-xl"
        />
        <motion.div
          variants={floatingVariants}
          animate="animate"
          className="absolute bottom-20 right-20 w-40 h-40 bg-white/10 rounded-full blur-xl"
          style={{ animationDelay: '2s' }}
        />
        <motion.div
          variants={floatingVariants}
          animate="animate"
          className="absolute top-1/2 left-1/4 w-24 h-24 bg-white/10 rounded-full blur-xl"
          style={{ animationDelay: '4s' }}
        />
      </div>

      <div className="container-custom relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center"
        >
          <motion.div
            variants={itemVariants}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Ready to Transform Your Career?
            </h2>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">
              Join thousands of successful engineers who have already started their journey with Prime Edge. 
              Your future in engineering starts here.
            </p>
          </motion.div>

          {/* Benefits Grid */}
          {/* <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12 max-w-4xl mx-auto"
          >
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="flex items-center space-x-3 text-blue-100"
              >
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span className="text-sm md:text-base">{benefit}</span>
              </motion.div>
            ))}
          </motion.div> */}

          {/* CTA Buttons */}
          {/* <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
          >
            <Link
              to="/courses"
              className="group btn btn-secondary btn-xl inline-flex items-center bg-white text-primary-600 hover:bg-gray-100"
            >
              Browse All Courses
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
            
            <Link
              to="/register"
              className="group btn btn-outline btn-xl inline-flex items-center border-2 border-white text-white hover:bg-white hover:text-primary-600"
            >
              Start Free Trial
              <Zap className="w-5 h-5 ml-2 group-hover:scale-110 transition-transform" />
            </Link>
          </motion.div> */}

          {/* Trust Indicators */}
          {/* <motion.div
            variants={itemVariants}
            className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto"
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="w-8 h-8 text-white" />
              </div>
              <div className="text-2xl font-bold text-white mb-1">
                {company?.stats?.studentsTrained ? company.stats.studentsTrained.toLocaleString() + '+' : '5000+'}
              </div>
              <div className="text-blue-100 text-sm">Students</div>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <div className="text-2xl font-bold text-white mb-1">
                {company?.stats?.coursesOffered ? company.stats.coursesOffered.toLocaleString() + '+' : '50+'}
              </div>
              <div className="text-blue-100 text-sm">Courses</div>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Award className="w-8 h-8 text-white" />
              </div>
              <div className="text-2xl font-bold text-white mb-1">
                {company?.stats?.successRate ? company.stats.successRate + '%' : '98%'}
              </div>
              <div className="text-blue-100 text-sm">Success Rate</div>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Star className="w-8 h-8 text-white" />
              </div>
              <div className="text-2xl font-bold text-white mb-1">
                {company?.stats?.averageRating ? company.stats.averageRating + '/5' : '4.9/5'}
              </div>
              <div className="text-blue-100 text-sm">Rating</div>
            </div>
          </motion.div> */}

          {/* Final CTA */}
          <motion.div
            variants={itemVariants}
            className="mt-12 p-8 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20"
          >
            <h3 className="text-2xl font-bold text-white mb-4">
              Join us
            </h3>
            <p className="text-blue-100 mb-6">
      Create your free account now and start exploring our courses — register today to secure your spot!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="group btn btn-primary btn-lg inline-flex items-center bg-white text-primary-600 hover:bg-gray-100"
              >
                <Shield className="w-5 h-5 mr-2" />
                Register Now
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <p className="text-blue-200 text-sm mt-4">
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default CtaSection;

